import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import FadeInSection from '../../../components/feature/FadeInSection';
import { supabase } from '../../../lib/supabaseClient';
import { BlogPost, BlogCategory } from '../../../types/blog';

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}

export default function BlogGrid() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const [{ data: cats }, { data: postsData }] = await Promise.all([
        supabase.from('8a4f04ed_blog_categories').select('*').order('created_at'),
        supabase
          .from('8a4f04ed_blog_posts')
          .select('*, category:8a4f04ed_blog_categories(*)')
          .eq('published', true)
          .order('published_at', { ascending: false }),
      ]);
      if (cats) setCategories(cats);
      if (postsData) setPosts(postsData as BlogPost[]);
      setLoading(false);
    };
    fetchData();
  }, []);

  const filtered =
    activeCategory === 'all'
      ? posts
      : posts.filter((p) => p.category_id === activeCategory);

  if (loading) {
    return (
      <section className="section-padding" style={{ background: '#fff8f2' }}>
        <div className="content-width">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl overflow-hidden animate-pulse">
                <div className="w-full h-[200px]" style={{ background: '#f0e8e0' }} />
                <div className="p-6">
                  <div className="h-4 rounded mb-3" style={{ background: '#f0e8e0', width: '40%' }} />
                  <div className="h-5 rounded mb-2" style={{ background: '#f0e8e0' }} />
                  <div className="h-5 rounded mb-4" style={{ background: '#f0e8e0', width: '70%' }} />
                  <div className="h-4 rounded" style={{ background: '#f0e8e0' }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="section-padding" style={{ background: '#fff8f2' }}>
      <div className="content-width">
        {/* Category Filter */}
        <FadeInSection className="flex flex-wrap justify-center gap-3 mb-12">
          <button
            onClick={() => setActiveCategory('all')}
            className="px-5 py-2 rounded-full text-sm font-bold transition-all duration-200 cursor-pointer whitespace-nowrap"
            style={
              activeCategory === 'all'
                ? { background: '#5a9e6f', color: '#fff' }
                : { background: '#fff', color: '#6b5a4e', border: '1px solid #d4c4ba' }
            }
          >
            すべて
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className="px-5 py-2 rounded-full text-sm font-bold transition-all duration-200 cursor-pointer whitespace-nowrap"
              style={
                activeCategory === cat.id
                  ? { background: cat.color, color: '#fff' }
                  : { background: '#fff', color: '#6b5a4e', border: '1px solid #d4c4ba' }
              }
            >
              {cat.name}
            </button>
          ))}
        </FadeInSection>

        {/* Blog Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filtered.map((post, index) => {
            const cat = post.category;
            return (
              <FadeInSection key={post.id} delay={index * 60}>
                <Link
                  to={`/blog/${post.id}`}
                  className="block bg-white rounded-xl overflow-hidden cursor-pointer h-full"
                  style={{ transition: 'transform 0.3s, box-shadow 0.3s' }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.transform = 'translateY(-5px)';
                    (e.currentTarget as HTMLElement).style.boxShadow = '0 10px 28px rgba(0,0,0,0.10)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                    (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                  }}
                >
                  <div className="w-full h-[200px] overflow-hidden">
                    {post.thumbnail_url ? (
                      <img
                        src={post.thumbnail_url}
                        alt={post.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105"
                        loading="lazy"
                        width={400}
                        height={200}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center" style={{ background: '#f0e8e0' }}>
                        <i className="ri-image-line text-4xl" style={{ color: '#c4b4a8' }}></i>
                      </div>
                    )}
                  </div>
                  <div className="p-6 flex flex-col h-[calc(100%-200px)]">
                    <div className="flex items-center gap-2 mb-3">
                      {cat && (
                        <span
                          className="inline-block px-3 py-0.5 rounded-full text-xs font-bold"
                          style={{ background: cat.color, color: '#fff' }}
                        >
                          {cat.name}
                        </span>
                      )}
                      <span style={{ color: '#9b8b7f', fontSize: '13px' }}>
                        {formatDate(post.published_at)}
                      </span>
                    </div>
                    <h3
                      className="mb-3 leading-snug"
                      style={{ color: '#4a3728', fontSize: '17px', fontWeight: '700' }}
                    >
                      {post.title}
                    </h3>
                    <p
                      className="line-clamp-3 flex-1"
                      style={{ color: '#6b5a4e', fontSize: '14px', lineHeight: '1.8' }}
                    >
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between mt-4 pt-4" style={{ borderTop: '1px solid #f0e8e0' }}>
                      <div className="flex items-center gap-2">
                        <span className="flex items-center justify-center w-5 h-5">
                          <i className="ri-user-line text-sm" style={{ color: '#9b8b7f' }}></i>
                        </span>
                        <span style={{ color: '#9b8b7f', fontSize: '13px' }}>{post.author_name}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="flex items-center justify-center w-4 h-4">
                          <i className="ri-time-line text-xs" style={{ color: '#9b8b7f' }}></i>
                        </span>
                        <span style={{ color: '#9b8b7f', fontSize: '13px' }}>約{post.read_minutes}分</span>
                      </div>
                    </div>
                  </div>
                </Link>
              </FadeInSection>
            );
          })}
        </div>

        {!loading && filtered.length === 0 && (
          <div className="text-center py-20">
            <p style={{ color: '#9b8b7f', fontSize: '17px' }}>
              このカテゴリーの記事はまだありません。
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
